import { c as _c } from "react/compiler-runtime";
import React, { ChangeEvent, KeyboardEvent, ReactNode, startTransition, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { Trans, useTranslation } from 'react-i18next';
import { components } from '@/api/skippay/generated/selfCare';
import { usePublicUpsellServicesPriceCalculator } from '@/api/skippay/publicUpsellServicesPriceCalculator';
import DeviceMobileIcon from '@/assets/icons/heroIcons/Device-mobile.svg';
import DeviceTabletIcon from '@/assets/icons/heroIcons/Device-tablet.svg';
import DesktopComputerIcon from '@/assets/icons/heroIcons/Desktop-computer.svg';
import CubeIcon from '@/assets/icons/heroIcons/Cube.svg';
import CameraIcon from '@/assets/icons/heroIcons/Camera.svg';
import { Button } from '@/components/buttons';
import { CalculatorNumberInput } from '@/components/inputs';
import { Link } from '@/components/links';
import { RangeSlider } from '@/components/RangeSlider';
import { Modal } from '@/components/modal';
import { SectionHeadline } from '@/components/SectionHeadline';
import { Headline, Text } from '@/components/typography';
import { Language } from '@/constants/currencyConfig';
import { LINKS } from '@/constants/links';
import { formatApiAmount } from '@/utils/amount';
import { formatAmount, formatNum } from '@/utils/formatting';
import { mergeClasses } from '@/utils/style';
import { InsuranceCategoryButton } from './InsuranceCategoryButton';
const PRICE_MIN = 1;
const PRICE_MAX = 31_000;
const DEFAULT_PRICE = 1_000;
const DEFAULT_CATEGORY = 'WHITE_GOODS';
const PRICE_FIELD_NAME = 'insurancePurchasedItemPrice';
const ACCEPT_WHERE_TO_SHOP_CATEGORY_ID = '8jKCaG6R1hfJCi5zQT6j7o';
const PROD_WHERE_TO_SHOP_CATEGORY_ID = 'RN8ofWXDYkPrqxArdJ7Vw3';
type InsuranceCategory = components['schemas']['UpsellServicePurchasedItemCategoryEnum'];
type InsuranceConfiguration = components['schemas']['UpsellServiceConfiguration'];
type InsuranceCategoryDefinition = {
  id: InsuranceCategory;
  icon: ReactNode;
  labelKey: 'calculator.categories.phones.label' | 'calculator.categories.computers.label' | 'calculator.categories.whiteGoods.label' | 'calculator.categories.tv.label' | 'calculator.categories.other.label';
  helpKey: 'calculator.categories.phones.help' | 'calculator.categories.computers.help' | 'calculator.categories.whiteGoods.help' | 'calculator.categories.tv.help' | 'calculator.categories.other.help';
};
const categories: InsuranceCategoryDefinition[] = [{
  id: 'PHONES',
  icon: <DeviceMobileIcon className="h-12 w-12 md:h-6 md:w-6" />,
  labelKey: 'calculator.categories.phones.label',
  helpKey: 'calculator.categories.phones.help'
}, {
  id: 'COMPUTERS',
  icon: <DeviceTabletIcon className="h-12 w-12 md:h-6 md:w-6" />,
  labelKey: 'calculator.categories.computers.label',
  helpKey: 'calculator.categories.computers.help'
}, {
  id: 'WHITE_GOODS',
  icon: <CubeIcon className="h-12 w-12 md:h-6 md:w-6" />,
  labelKey: 'calculator.categories.whiteGoods.label',
  helpKey: 'calculator.categories.whiteGoods.help'
}, {
  id: 'TV',
  icon: <DesktopComputerIcon className="h-12 w-12 md:h-6 md:w-6" />,
  labelKey: 'calculator.categories.tv.label',
  helpKey: 'calculator.categories.tv.help'
}, {
  id: 'OTHER',
  icon: <CameraIcon className="h-12 w-12 md:h-6 md:w-6" />,
  labelKey: 'calculator.categories.other.label',
  helpKey: 'calculator.categories.other.help'
}];
function getWhereToShopLink(): string {
  const categoryId = process.env.NEXT_PUBLIC_TARGET_ENV === 'prod' ? PROD_WHERE_TO_SHOP_CATEGORY_ID : ACCEPT_WHERE_TO_SHOP_CATEGORY_ID;
  return `${LINKS().whereToShop}?cat=${categoryId}`;
}
function normalizePrice(value: number): number {
  return Math.min(Math.max(Math.ceil(value), PRICE_MIN), PRICE_MAX);
}
function sortConfigurations(configurations: InsuranceConfiguration[]): InsuranceConfiguration[] {
  return [...configurations].sort((a, b) => {
    const serviceOrder = {
      DAMAGE_AND_THEFT: 0,
      EXTENDED_WARRANTY: 1
    } as const;
    const byService = serviceOrder[a.service as keyof typeof serviceOrder] - serviceOrder[b.service as keyof typeof serviceOrder];
    if (byService !== 0) {
      return byService;
    }
    return (a.duration ?? 0) - (b.duration ?? 0);
  });
}
function getPluralSuffix(count: number, one: string, few: string, other: string): string {
  if (count === 1) {
    return one;
  }
  if (count >= 2 && count <= 4) {
    return few;
  }
  return other;
}
function ResultTile(t0) {
  const $ = _c(29);
  const {
    configuration,
    language
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["insuranceAndWarranty"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const isDamageAndTheft = configuration.service === "DAMAGE_AND_THEFT";
  let t2;
  if ($[1] !== isDamageAndTheft || $[2] !== t) {
    t2 = isDamageAndTheft ? t("calculator.services.damageAndTheft") : t("calculator.services.extendedWarranty");
    $[1] = isDamageAndTheft;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const serviceTitle = t2;
  if (!configuration.duration || !configuration.price?.amount) {
    return null;
  }
  let t3;
  if ($[4] !== configuration.duration || $[5] !== configuration.durationUnit || $[6] !== t) {
    t3 = configuration.durationUnit === "DAY" ? `${configuration.duration} ${getPluralSuffix(configuration.duration, t("calculator.duration.day_one"), t("calculator.duration.day_few"), t("calculator.duration.day_other"))}` : configuration.durationUnit === "MONTH" ? `${configuration.duration} ${getPluralSuffix(configuration.duration, t("calculator.duration.month_one"), t("calculator.duration.month_few"), t("calculator.duration.month_other"))}` : `${configuration.duration} ${getPluralSuffix(configuration.duration, t("calculator.duration.year_one"), t("calculator.duration.year_few"), t("calculator.duration.year_other"))}`;
    $[4] = configuration.duration;
    $[5] = configuration.durationUnit;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const durationLabel = t3;
  const t4 = isDamageAndTheft ? "bg-purple-500" : "bg-blue-500";
  let t5;
  if ($[8] !== t4) {
    t5 = mergeClasses("flex min-h-20 flex-row items-center justify-between gap-1 rounded-2xl px-6 py-4 text-center text-white sm:flex-col sm:justify-center md:min-h-48", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== serviceTitle) {
    t6 = <Text>{serviceTitle}</Text>;
    $[10] = serviceTitle;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== durationLabel) {
    t7 = <Text className="block sm:hidden">{durationLabel}</Text>;
    $[12] = durationLabel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex flex-col items-start sm:items-center">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== configuration.price.amount || $[18] !== language) {
    t9 = formatAmount(formatApiAmount(configuration.price.amount), language);
    $[17] = configuration.price.amount;
    $[18] = language;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <Headline as="h4" variant="h4" className="text-[2rem]!">{t9}</Headline>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== durationLabel) {
    t11 = <Text className="hidden sm:block">{durationLabel}</Text>;
    $[22] = durationLabel;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t5 || $[27] !== t8) {
    t12 = <div className={t5}>{t8}{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t5;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
export function Calculator() {
  const $ = _c(90);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["insuranceAndWarranty", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation(t0);
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);
  const [productPrice, setProductPrice] = useState(DEFAULT_PRICE);
  const [debouncedPrice, setDebouncedPrice] = useState(DEFAULT_PRICE);
  const [isHelpModalOpened, setIsHelpModalOpened] = useState(false);
  const categoryContainerRef = useRef(null);
  const categoryButtonRefs = useRef(new Map());
  const hasCenteredDefaultCategoryRef = useRef(false);
  let t1;
  if ($[1] !== productPrice) {
    t1 = () => setDebouncedPrice(productPrice);
    $[1] = productPrice;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== productPrice) {
    t2 = [productPrice];
    $[3] = productPrice;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useDebounce(t1, 300, t2);
  const t3 = debouncedPrice * 100;
  let t4;
  if ($[5] !== selectedCategory || $[6] !== t3) {
    t4 = {
      purchasedItemPrice: t3,
      purchasedItemCategory: selectedCategory
    };
    $[5] = selectedCategory;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      enabled: true,
      staleTime: 300000
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data,
    isError,
    isFetching,
    refetch
  } = usePublicUpsellServicesPriceCalculator(t4, t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => {
      const nextValue = typeof value === "number" ? value : typeof value === "string" ? parseFloat(value) : parseFloat(value.target.value);
      startTransition(() => {
        setProductPrice(normalizePrice(isNaN(nextValue) ? PRICE_MIN : nextValue));
      });
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handlePriceChange = t6;
  const handlePriceChangeOnEnter = _temp;
  const visibleConfigurations = sortConfigurations((data ?? []).filter(_temp2));
  const centerDefaultCategory = () => {
    let attempts = 0;
    const tryCenterDefaultCategory = () => {
      const container = categoryContainerRef.current;
      const selectedButton = categoryButtonRefs.current.get(DEFAULT_CATEGORY);
      if (!container || !selectedButton) {
        if (attempts < 10) {
          attempts = attempts + 1;
          attempts;
          requestAnimationFrame(tryCenterDefaultCategory);
        }
        return;
      }
      if (container.scrollWidth <= container.clientWidth) {
        return;
      }
      const targetLeft = selectedButton.offsetLeft - container.clientWidth / 2 + selectedButton.clientWidth / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "auto"
      });
    };
    requestAnimationFrame(tryCenterDefaultCategory);
  };
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsHelpModalOpened(false);
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t("calculator.helpModal.title");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <Headline as="h2" variant="h6">{t8}</Headline>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = categories.map(category => <div key={category.id}><Text variant="p3" weight="semibold" className="mb-1">{t(category.labelKey)}</Text><Text variant="p3" className="text-gray-700">{t(category.helpKey)}</Text></div>);
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <div className="flex flex-col gap-3">{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== isHelpModalOpened || $[21] !== t11) {
    t12 = <Modal isVisible={isHelpModalOpened} onClose={t7} modalName="insuranceCategoryHelp" analyticsParam="insuranceCategoryHelp" testId="insuranceCategoryHelp" className="max-w-[48rem] rounded-4xl p-10">{t11}</Modal>;
    $[20] = isHelpModalOpened;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = "relative z-10 px-2";
  let t14;
  if ($[23] !== t) {
    t14 = t("calculator.title");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <SectionHeadline className="mb-10 px-4 md:mb-16" variant="inverted" anchorKey="calculator.title">{t14}</SectionHeadline>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = "mx-auto flex max-w-[60.5rem] flex-col gap-6 rounded-2xl bg-blue-100 p-4 md:gap-12 md:rounded-[2.5rem] md:p-12";
  let t17;
  if ($[27] !== t) {
    t17 = t("calculator.categoryLabel");
    $[27] = t;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17) {
    t18 = <Headline as="h3" variant="h6" anchorKey="calculator.categoryLabel" className="text-gray-900">{t17}</Headline>;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsHelpModalOpened(true);
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t) {
    t20 = t("calculator.helpCta");
    $[32] = t;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t20) {
    t21 = <Button variant="unstyled" onClick={t19} testId="insuranceCalculatorCategoryHelp" analyticsParam="insuranceCalculatorCategoryHelp" className="w-fit text-left text-base text-blue-500 underline">{t20}</Button>;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t18 || $[37] !== t21) {
    t22 = <div className="flex flex-row items-center justify-between">{t18}{t21}</div>;
    $[36] = t18;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== centerDefaultCategory) {
    t23 = node => {
      categoryContainerRef.current = node;
      if (node && !hasCenteredDefaultCategoryRef.current) {
        hasCenteredDefaultCategoryRef.current = true;
        centerDefaultCategory();
      }
    };
    $[39] = centerDefaultCategory;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t) {
    t24 = t("calculator.categoryLabel");
    $[41] = t;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = categories.map(category_0 => <InsuranceCategoryButton key={category_0.id} icon={category_0.icon} label={t(category_0.labelKey)} checked={selectedCategory === category_0.id} onChange={() => setSelectedCategory(category_0.id)} containerRef={node_0 => {
    categoryButtonRefs.current.set(category_0.id, node_0);
  }} />);
  let t26;
  if ($[43] !== t23 || $[44] !== t24 || $[45] !== t25) {
    t26 = <div ref={t23} role="radiogroup" aria-label={t24} className="-mx-4 flex snap-x gap-3.5 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 lg:grid-cols-3">{t25}</div>;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t22 || $[48] !== t26) {
    t27 = <div className="flex flex-col gap-4">{t22}{t26}</div>;
    $[47] = t22;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t) {
    t28 = t("calculator.priceLabel");
    $[50] = t;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t28) {
    t29 = <Headline as="h3" variant="h6" anchorKey="calculator.priceLabel" className="text-gray-900">{t28}</Headline>;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = handlePriceChangeOnEnter(value_0 => handlePriceChange(value_0));
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== productPrice) {
    t31 = <CalculatorNumberInput value={productPrice} onKeyPress={t30} max={PRICE_MAX} min={PRICE_MIN} name={PRICE_FIELD_NAME} onBlur={handlePriceChange} withCurrency={true} />;
    $[55] = productPrice;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t29 || $[58] !== t31) {
    t32 = <div className="mb-3 flex flex-row items-center justify-between gap-3 md:mb-4">{t29}{t31}</div>;
    $[57] = t29;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== i18n.language || $[61] !== t) {
    t33 = t("calculator.maxPrice", {
      value: formatNum(PRICE_MAX, undefined, i18n.language) ?? PRICE_MAX.toString()
    });
    $[60] = i18n.language;
    $[61] = t;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== i18n.language || $[64] !== t) {
    t34 = t("calculator.minPrice", {
      value: formatNum(PRICE_MIN, undefined, i18n.language) ?? PRICE_MIN.toString()
    });
    $[63] = i18n.language;
    $[64] = t;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== productPrice || $[67] !== t33 || $[68] !== t34) {
    t35 = <RangeSlider max={PRICE_MAX} maxLabel={t33} min={PRICE_MIN} minLabel={t34} name={PRICE_FIELD_NAME} onBlur={handlePriceChange} onChange={handlePriceChange} step={1} value={productPrice} aria-valuemin={PRICE_MIN} aria-valuemax={PRICE_MAX} aria-valuenow={productPrice} />;
    $[66] = productPrice;
    $[67] = t33;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== t32 || $[71] !== t35) {
    t36 = <div>{t32}{t35}</div>;
    $[70] = t32;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  const t37 = isError ? <div className="rounded-2xl bg-white p-4 text-center md:p-6"><Text variant="p3" className="mb-3 text-gray-900">{t("core:queryError.title")}</Text><Text variant="p4" className="mb-4 text-gray-700"><Trans i18nKey="core:queryError.text" t={t}><Link to={LINKS().contact} testId="insuranceCalculatorErrorContact" analyticsParam="insuranceCalculatorErrorContact" /></Trans></Text><Button variant="secondary" onClick={() => void refetch()} testId="insuranceCalculatorRetry" analyticsParam="insuranceCalculatorRetry" className="w-full md:w-auto" busy={isFetching}>{t("core:queryError.button")}</Button></div> : visibleConfigurations.length > 0 && <div><Headline as="h3" variant="h6" anchorKey="calculator.resultsLabel" className="mb-4 text-gray-900">{t("calculator.resultsLabel")}</Headline><div className="grid gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 md:gap-6">{visibleConfigurations.map(configuration_0 => <ResultTile key={`${configuration_0.service}-${configuration_0.duration}-${configuration_0.price?.amount}`} configuration={configuration_0} language={i18n.language as Language} />)}</div></div>;
  let t38;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = getWhereToShopLink();
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t) {
    t39 = t("calculator.cta");
    $[74] = t;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== t39) {
    t40 = <Button to={t38} testId="insuranceCalculatorWhereToShop" analyticsParam="insuranceCalculatorWhereToShop" size="lg" variant="primary" align="center" className="mx-auto w-full md:w-70">{t39}</Button>;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t27 || $[79] !== t36 || $[80] !== t37 || $[81] !== t40) {
    t41 = <div className={t16}>{t27}{t36}{t37}{t40}</div>;
    $[78] = t27;
    $[79] = t36;
    $[80] = t37;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t15 || $[84] !== t41) {
    t42 = <div className={t13}>{t15}{t41}</div>;
    $[83] = t15;
    $[84] = t41;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="bgCircle absolute top-[10%] left-2/3" />;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t12 || $[88] !== t42) {
    t44 = <>{t12}{t42}{t43}</>;
    $[87] = t12;
    $[88] = t42;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  return t44;
}
function _temp2(configuration) {
  return configuration.duration && configuration.price?.amount && configuration.service;
}
function _temp(changeHandler) {
  return e => {
    const keyCode = e.which || e.keyCode;
    if (keyCode === 13) {
      changeHandler(e.currentTarget.value.toString());
    }
  };
}